<template>
  <div>
    <b-modal
      centered
      size="md"
      no-close-on-esc
      no-close-on-backdrop
      v-model="modalVisivel"
      @hidden="onModalClose"
      :hide-footer="isGerandoRelatorio"
      :hide-header-close="isGerandoRelatorio"
    >
      <template #modal-title>
        {{ isGerandoRelatorio ? "Processando Dados" : "Folha de Produção" }}
      </template>

      <div v-if="isGerandoRelatorio" class="text-center py-2">
        <div class="d-flex justify-content-center align-items-center mb-3">
          <b-spinner class="mb-2" variant="primary" />
        </div>
        <h5 class="font-weight-bold mb-3">{{ mensagemStatus }}</h5>

        <b-progress
          :max="100"
          class="mt-3"
          height="20px"
          variant="success"
          :value="progresso"
          show-progress
        >
          <b-progress-bar
            :striped="progresso < 100"
            :animated="progresso < 100"
            :value="progresso"
            :label="progresso === 100 ? 'Concluído!' : `${progresso}%`"
          />
        </b-progress>
      </div>

      <div v-else>
        <div class="row d-flex">
          <div class="mb-1">
            <b-form-group
              label="Mês Referência"
              :state="stateMesReferencia"
              :invalid-feedback="feedbackMesReferencia"
            >
              <multiselect
                :searchable="true"
                :loading="loadingMeses"
                track-by="dt_referencia"
                @input="onMesSelecionado"
                selected-label=""
                v-model="selecaoMesReferencia"
                :options="optionMesReferencia"
                :disabled="!temCooperadoSelecionado"
                :custom-label="formatarDataMesReferencia"
                placeholder="Selecione o mês de referência"
                deselect-label=""
                select-label=""
              >
                <span slot="noResult">Nenhum mês de referência encontrado</span>
                <span slot="noOptions"
                  >Nenhum mês de referência disponível</span
                >
              </multiselect>
            </b-form-group>
          </div>
          <div class="mt-1">
            <b-form-group
              label="Projeto"
              :state="stateProjeto"
              :invalid-feedback="feedbackProjeto"
            >
              <multiselect
                :searchable="true"
                track-by="sq_folha"
                v-model="selecaoProjeto"
                :options="projetoOptions"
                :loading="loadingProjetos"
                selected-label=""
                :custom-label="formatarProjeto"
                placeholder="Selecione um projeto"
                deselect-label=""
                select-label=""
                :disabled="!selecaoMesReferencia || loadingProjetos"
              >
                <span slot="noResult">
                  Nenhum projeto encontrado para este mês de referência
                </span>
                <span slot="noOptions">
                  Nenhum projeto disponível para este mês de referência
                </span>
              </multiselect>
            </b-form-group>
          </div>
        </div>
        <div
          v-if="!temCooperadoSelecionado && modalVisivel"
          class="alert alert-warning mt-3"
        >
          <i class="mdi mdi-alert-circle-outline me-2"></i>
          Para gerar a Folha de Produção, primeiro selecione um Cooperado nos
          filtros principais.
        </div>
      </div>

      <template v-slot:modal-footer>
        <div class="w-100 d-flex align-items-center justify-content-end gap-2">
          <b-button
            variant="success"
            @click="consultar"
            :disabled="!podeGerarRelatorio"
          >
            <i class="fas fa-download me-1"></i>
            <span>Gerar Relatório</span>
          </b-button>

          <b-button
            class="button-fechar-modal"
            v-if="!isGerandoRelatorio"
            variant="secondary"
            @click="fecharModal"
          >
            <i class="mdi mdi-close me-1"></i>
            <span>Fechar</span>
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script lang="js">
import Multiselect from "vue-multiselect";
import { ProjetosService } from "../../../../../services/astec/projetos";
import { FolhaProducaoService } from "../../../../../services/astec/folhaProducao";

export default {
  name: "RelatorioFolhaProducao",
  components: { Multiselect },

  props: {
    isOpenModal: {
      type: Boolean,
      default: false
    },
    filtrosDeProjetos: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      progresso: 0,
      buscaTimeout: null,
      loadingMeses: false,
      modalVisivel: false,
      selecaoProjeto: null,
      loadingProjetos: false,
      progressInterval: null,
      mostrarValidacao: true,
      optionMesReferencia: [],
      isGerandoRelatorio: false,
      selecaoMesReferencia: null,
      jaCarregouDoVuexNestaSessao: false,
      mensagemStatus: "Por favor, aguarde enquanto geramos seu relatório..",
    };
  },

  watch: {
    isOpenModal(valorModal) {
      if (valorModal) {
        this.modalVisivel = valorModal;
        this.mostrarValidacao = true;

        if (!this.jaCarregouDoVuexNestaSessao) {
          this.carregarFiltrosDoVuex();
          this.jaCarregouDoVuexNestaSessao = true;
        } else {
          this.limparSelecoes();
        }

        this.verificarECarregarMeses();
      } else {
        this.mostrarValidacao = false;
      }
    },

    temCooperadoSelecionado: {
      handler(temCooperado) {
        if (temCooperado && this.modalVisivel) {
          this.carregarMeses();
        }
      }
    },

    modalVisivel: {
      handler(estaVisivel) {
        if (estaVisivel && this.temCooperadoSelecionado) {
          this.carregarMeses();
        }
      }
    },

    selecaoMesReferencia() {
      this.selecaoProjeto = null;
      if (this.selecaoMesReferencia) {
        this.mostrarValidacao = false;
      }

      if (this.selecaoMesReferencia && this.temCooperadoSelecionado) {
        this.buscarProjetosPorMesDeReferencia();
      }
    },

    selecaoProjeto() {
      if (this.selecaoProjeto) {
        this.mostrarValidacao = false;
      }
    },
  },

  computed: {
    referenciaCooperado() {
      return this.filtrosRecebidos.cooperado?.value || null;
    },

    temCooperadoSelecionado() {
      return this.referenciaCooperado !== null;
    },

    projetoOptions() {
      if (!this.selecaoMesReferencia) return [];
      return this.selecaoMesReferencia.projetos || [];
    },

    stateMesReferencia() {
      if (this.selecaoMesReferencia) return true;
      if (this.modalVisivel && this.temCooperadoSelecionado && !this.isGerandoRelatorio) {
        return false;
      }
      return null;
    },

    stateProjeto() {
      if (this.selecaoProjeto) return true;
      if (this.mostrarValidacao && !this.selecaoProjeto && this.selecaoMesReferencia) {
        return false;
      }
      return null;
    },

    feedbackMesReferencia() {
      if (this.stateMesReferencia === false) {
        return "O campo mês de referência é obrigatório.";
      }
      return "";
    },

    feedbackProjeto() {
      if (this.stateProjeto === false) {
        return "O campo projeto é obrigatório.";
      }
      return "";
    },

    podeGerarRelatorio() {
      return this.temCooperadoSelecionado &&
        this.selecaoMesReferencia !== null &&
        this.selecaoProjeto !== null;
    },

    filtrosRecebidos() {
      const filtros = {};

      Object.keys(this.filtrosDeProjetos).forEach(key => {
        const valor = this.filtrosDeProjetos[key];
        if (valor !== null && valor !== undefined && valor !== '') {
          if (typeof valor === 'object') {
            if (Object.keys(valor).length > 0) {
              filtros[key] = valor;
            }
          } else {
            filtros[key] = valor;
          }
        }
      });

      return filtros;
    },
  },

  methods: {
    carregarFiltrosDoVuex() {
      this.limparSelecoes();

      if (this.temCooperadoSelecionado) {
        this.carregarMeses();
      } else {
        console.log("Folha de Produção - Nenhum cooperado selecionado nos filtros");
      }
    },

    onMesSelecionado() {
      if (this.buscaTimeout) {
        clearTimeout(this.buscaTimeout);
      }

      this.selecaoProjeto = null;

      this.buscaTimeout = setTimeout(() => {
        if (this.selecaoMesReferencia && this.temCooperadoSelecionado) {
          this.buscarProjetosPorMesDeReferencia();
        }
      }, 300);
    },

    consultar() {
      this.mostrarValidacao = true;
      this.processarRelatorio();
    },

    iniciarProgressoFake() {
      this.progresso = 0;

      this.progressInterval = setInterval(() => {
        if (this.progresso < 30) {
          this.progresso += 4;
        } else if (this.progresso < 70) {
          this.progresso += 2;
        } else if (this.progresso < 92) {
          this.progresso += 0.5;
        }

        if (this.progresso < 40) {
          this.mensagemStatus = "Coletando dados...";
        } else if (this.progresso < 75) {
          this.mensagemStatus = "Processando informações...";
        } else {
          this.mensagemStatus = "Gerando PDF...";
        }
      }, 800);
    },

    async processarRelatorio() {
      this.isGerandoRelatorio = true;
      this.mensagemStatus = "Gerando relatório...";
      this.iniciarProgressoFake();

      const inicio = Date.now();
      let pdfUrl = null;

      try {
        const response = await FolhaProducaoService.getFolhaProducao({
          cdPessoa: this.referenciaCooperado,
          sqFolha: this.selecaoProjeto.sq_folha,
        });

        const tempoMinimo = 2000;
        const decorrido = Date.now() - inicio;
        if (decorrido < tempoMinimo) {
          await this.delay(tempoMinimo - decorrido);
        }

        clearInterval(this.progressInterval);
        this.progresso = 95;
        this.mensagemStatus = "Finalizando PDF...";

        const base64 = response.base64;
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        const pdfBlob = new Blob([byteArray], { type: "application/pdf" });

        pdfUrl = URL.createObjectURL(pdfBlob);

        window.open(pdfUrl, "_blank");

        this.progresso = 100;
        this.mensagemStatus = "Concluído!";

        setTimeout(() => {
          this.$tools.alerta("Relatório gerado com sucesso!", "success");
          this.isGerandoRelatorio = false;
          this.modalVisivel = false;
          this.progresso = 0;
          this.limparSelecoes();
          this.$emit("fechar");
        }, 1500);

      } catch (error) {
        console.error("Erro ao gerar relatório de Folha de Produção:", error);

        let mensagemErro =
          "Erro ao gerar relatório de folha de produção.";
        if (error.response && error.response.status === 404) {
          mensagemErro =
            "Nenhum dado de folha de produção encontrado para os filtros selecionados.";
        }

        this.$tools.alerta(mensagemErro, "error");
        this.isGerandoRelatorio = false;

      } finally {
        clearInterval(this.progressInterval);

        if (pdfUrl) {
          URL.revokeObjectURL(pdfUrl);
        }

        this.mensagemStatus = "Por favor, aguarde enquanto geramos seu relatório...";
        this.limparSelecoes();
      }
    },

    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },

    async carregarMeses() {
      if (!this.temCooperadoSelecionado) return;

      this.loadingMeses = true;

      try {
        const response = await ProjetosService.getLkpMesReferencia(this.referenciaCooperado);
        const resultMes = response.data || response;

        this.optionMesReferencia = resultMes.map(item => ({
          dt_referencia: item.dt_referencia,
          sq_folha: item.sq_folha || null,
          projetos: []
        }));
      } catch (error) {
        console.error("Erro ao carregar meses de referência:", error);
        this.$tools.alerta("Erro ao carregar meses de referência", "error");

      } finally {
        this.loadingMeses = false;
      }
    },

    async buscarProjetosPorMesDeReferencia() {
      if (!this.selecaoMesReferencia || !this.temCooperadoSelecionado) {
        return;
      }

      this.loadingProjetos = true;
      this.selecaoProjeto = null;

      try {
        const dataFormatada = this.formatarDataParaAPI(this.selecaoMesReferencia.dt_referencia);

        const response = await ProjetosService.getLkpProjetosPorMesReferencia({
          cdPessoa: this.referenciaCooperado,
          dtReferencia: dataFormatada
        });

        this.selecaoMesReferencia.projetos = response.data || response || [];
      } catch (error) {
        console.error("Erro ao carregar projetos:", error);
        if (this.selecaoMesReferencia) {
          this.selecaoMesReferencia.projetos = [];
        }

        let errorMessage = "Erro ao carregar projetos";
        if (error.message && error.message.includes('Formato de data')) {
          errorMessage = `Data inválida: "${this.selecaoMesReferencia.dt_referencia}". Use formato DD/MM/YYYY.`;
        } else if (error.response?.status === 422) {
          errorMessage = "Dados inválidos para busca";
        }

        this.$tools.alerta(errorMessage, "error");

      } finally {
        this.loadingProjetos = false;
      }
    },

    formatarDataMesReferencia(option) {
      if (!option || !option.dt_referencia) return "";
      const data = new Date(option.dt_referencia.replace(" ", "T"));
      const mes = data.toLocaleString('pt-BR', { month: 'long' });
      const ano = data.getFullYear();
      const mesFormatado = mes.charAt(0).toUpperCase() + mes.slice(1);

      return `${mesFormatado}/${ano}`;
    },

    formatarProjeto(item) {
      if (!item) return "";

      if (item.nm_projeto && item.cd_projeto) {
        return `${item.nm_projeto} [${item.cd_projeto}]`;
      } else if (item.projeto) {
        return item.projeto;
      }

      return "Projeto sem nome";
    },

    limparSelecoes() {
      this.selecaoMesReferencia = null;
      this.selecaoProjeto = null;
      this.optionMesReferencia = [];

      this.mostrarValidacao = false;

      if (this.buscaTimeout) {
        clearTimeout(this.buscaTimeout);
        this.buscaTimeout = null;
      }
    },

    fecharModal() {
      this.modalVisivel = false;
      this.$emit("fechar");
    },

    onModalClose() {
      if (this.progressInterval) {
        clearInterval(this.progressInterval);
      }

      if (this.buscaTimeout) {
        clearTimeout(this.buscaTimeout);
      }

      this.isGerandoRelatorio = false;
      this.progresso = 0;
      this.mensagemStatus = "Por favor, aguarde enquanto geramos seu relatório..";

      this.$emit("fechar");
      this.limparSelecoes();
    },

    formatarDataParaAPI(data) {
      if (!data) return '';

      // Formato DD-MM-YYYY, retorna como está
      if (data.match(/^\d{2}-\d{2}-\d{4}$/)) {
        return data;
      }

      // "2025-08-01 00:00:00"
      if (data.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
        const [dataPart] = data.split(' ');
        const [ano, mes, dia] = dataPart.split('-');
        return `${dia}-${mes}-${ano}`;
      }

      // "2025-08-01"
      if (data.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [ano, mes, dia] = data.split('-');
        return `${dia}-${mes}-${ano}`;
      }

      // "01/08/2025"
      if (data.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
        const [dia, mes, ano] = data.split('/');
        return `${dia}-${mes}-${ano}`;
      }

      // Objeto Date
      if (data instanceof Date) {
        const dia = String(data.getDate()).padStart(2, '0');
        const mes = String(data.getMonth() + 1).padStart(2, '0');
        const ano = data.getFullYear();
        return `${dia}-${mes}-${ano}`;
      }

      try {
        const date = new Date(data);
        if (!isNaN(date.getTime())) {
          const dia = String(date.getDate()).padStart(2, '0');
          const mes = String(date.getMonth() + 1).padStart(2, '0');
          const ano = date.getFullYear();
          return `${dia}-${mes}-${ano}`;
        }
      } catch (e) {
        console.warn('Não foi possível converter a data:', data);
      }
      return data;
    },
  },

  beforeDestroy() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
    }

    if (this.buscaTimeout) {
      clearTimeout(this.buscaTimeout);
    }
  }
};
</script>

<style lang="css" scoped>
.button-fechar-modal {
  width: 140px;
}
</style>
